import {
  Badge,
  Panel,
  PanelBody,
  PanelHeader,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TableWrap,
} from '@/components/ui';
import type { BadgeTone } from '@/components/ui';
import { formatDateOnly, formatDecimalString, orDash, pluralise } from '@/lib/format';
import {
  CLAWBACK_KIND_LABELS,
  CREDIT_DOES_NOT_REDUCE_REVENUE_NOTE,
  EXCLUDED_DOCUMENTS_NOTE,
  FROZEN_STATEMENT_NOTE,
  LIVE_ATTRIBUTION_NOTE,
  NEW_BUSINESS_HEADINGS,
  attainmentTone,
  clawbackAttributionNote,
  formatPercent,
  gateSummary,
  newBusinessGateBasisLabel,
  payoutBreakdown,
  type AttainmentTone,
  type PayoutRowKind,
} from '@/lib/commissions';
import type { CommissionStatement } from '@/lib/types';
import { NewBusinessAccountsTable } from './NewBusinessAccounts';

/* ---------------------------------------------------------------------------
 * ONE FROZEN COMMISSION STATEMENT, READ-ONLY.
 *
 * ⛔ A STATEMENT IS A SNAPSHOT, NOT A VIEW, AND NOTHING HERE MAY RESOLVE ONE OF
 * ITS FIELDS BY LOOKING SOMETHING UP. Every term, every date, every rate and
 * every figure was COPIED onto the row when the run computed (migration 204),
 * which is what makes an approved year re-readable as the figures that were
 * actually paid. Joining back to the scheme for a rate — however obviously
 * redundant the duplication looks — would re-price a year somebody was already
 * paid for, the next time anybody edited a template.
 *
 * ⛔ AND IT IS READ-ONLY BY CONTRACT, NOT BY OMISSION. There is no statement
 * write schema on the API at all: correcting a figure is correcting the SCHEME
 * or the ASSIGNMENT and recomputing the run, and after approval it is a NEW run.
 * Do not grow an editor here.
 *
 * ⛔ MONEY IS A DECIMAL STRING AND NEVER SEES A FLOAT. `formatDecimalString`,
 * never `Number()`: `0.1 + 0.2` is not `0.3`, and there is no plausibility check
 * on a commission figure that catches a rounding drift.
 * ------------------------------------------------------------------------- */

export interface StatementPanelProps {
  statement: CommissionStatement;
  /**
   * Whether the RUN this statement belongs to is approved.
   *
   * ⛔ THE TWO STANDING NOTES ARE MUTUALLY EXCLUSIVE AND ONLY THE RUN KNOWS
   * WHICH APPLIES. An approved run is frozen; a draft credits revenue to whoever
   * leads each account TODAY, so moving one company rewrites a closed year with
   * nothing erroring. Putting the frozen promise on a draft is a false statement
   * about figures that will move on the next recompute, and putting the draft's
   * warning on an approved year undermines the one sentence a reader needs to
   * trust.
   *
   * ⚠️ `undefined` RENDERS NEITHER, deliberately. A statement carries no
   * approval column of its own, so a caller that has not said is a caller that
   * does not know — and a panel that guessed would be wrong exactly half the
   * time, in the reassuring direction half of that.
   *
   * ⛔ Derive it from the run's `approvedAt` or its status flag, NEVER from the
   * presence of the approver: `approved_by_id` is `ON DELETE SET NULL`, so a
   * departed colleague would make an approved run read as unapproved. And read
   * `isApproved` rather than testing `isVoid` — the two are not complements,
   * because a draft is neither.
   */
  runApproved?: boolean;
  /**
   * Names the lead a clawback's SOURCE revenue was attributed to, where the
   * caller can resolve one.
   *
   * ⚠️ A CALLBACK RATHER THAN A FETCH, so this panel stays pure and a page
   * showing several statements pays for one staff read rather than one per
   * panel. Unresolved falls back to the id, which is still a fact somebody can
   * act on — never a blank, and never this statement's own lead.
   */
  resolveLeadName?: (userId: number) => string | null;
  className?: string;
}

/**
 * ⛔ `'unknown'` IS NOT A DEGRADED `'short'` AND MUST NOT BE PAINTED AS ONE.
 * *No regional commitment* and *missed the target* are different facts about a
 * person's year; the neutral tone says the first without accusing anybody of the
 * second.
 */
const TONE_BADGE: Record<AttainmentTone, BadgeTone> = {
  met: 'success',
  near: 'warning',
  short: 'danger',
  unknown: 'neutral',
};

/**
 * ⚠️ THE WORD IS WHY THE BADGE IS NOT COLOUR-ONLY. The house rule forbids colour
 * as the only signal, and a bare percentage beside a threshold printed in another
 * block is exactly that — a reader would have to hold two numbers in their head
 * to know which way it went.
 */
const TONE_WORD: Record<AttainmentTone, string | null> = {
  met: 'Met',
  near: 'Near',
  short: 'Short',
  unknown: null,
};

const NEW_BUSINESS_GATE_HEADINGS = {
  basis: 'New business gate',
  personalGate: 'New business personal gate',
  regionalGate: 'New business regional gate',
  shape: 'New business gate shape',
  factor: 'New business gate factor',
} as const;

/**
 * A gate factor (`"0"` to `"1"`, a decimal string) as a percentage string, by
 * moving the decimal point two places — exact, never through a float. An
 * unreadable value is returned as-is for `formatPercent` to print verbatim.
 */
function factorAsPercent(factor: string | null): string | null {
  const text = factor?.trim() ?? '';
  if (text === '') return null;
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) return text;
  const sign = match[1] ?? '';
  const fraction = (match[3] ?? '').padEnd(2, '0');
  const whole = `${match[2] ?? ''}${fraction.slice(0, 2)}`.replace(/^0+(?=\d)/, '');
  const rest = fraction.slice(2);
  return rest === '' ? `${sign}${whole}` : `${sign}${whole}.${rest}`;
}

/*
 * `CLAWBACK_KIND_LABELS` moved to `@/lib/commissions` on 2026-09-24, because
 * the runs page's unrecovered panel now names clawback kinds too, and two
 * copies of the wording would drift.
 */

export function StatementPanel({
  statement,
  runApproved,
  resolveLeadName,
  className,
}: StatementPanelProps) {
  const money = (value: string | null): string =>
    value === null ? '—' : `${formatDecimalString(value)} ${statement.currency}`;

  const leadName = statement.user.name ?? statement.user.email;

  /*
   * ⛔ TWO COUNTS, READ SEPARATELY AND NEVER ADDED. They are taken over
   * OVERLAPPING populations — a lead's own documents are part of their region's
   * — so a document can be counted in both, and one summed figure would report a
   * larger gap than the statement has. Each sentence renders beside the figure it
   * describes.
   *
   * ⛔ And neither is ever `?? 0`. A document with no exchange rate for its own
   * date is left OUT of the revenue and counted here, never converted at par, so
   * a commission can be computed on less revenue than was earned with nothing
   * else on the statement saying so. `0` is the healthy answer production gives
   * today, which is exactly why a reader has to exist for it — and why the helper
   * prints NOTHING at zero rather than a line that teaches somebody to stop
   * reading the small print.
   */
  const personalExcluded = EXCLUDED_DOCUMENTS_NOTE(statement.personalExcludedDocuments);
  const regionalExcluded = EXCLUDED_DOCUMENTS_NOTE(statement.regionalExcludedDocuments);

  const breakdown = payoutBreakdown(statement);

  /*
   * ⚠️ NEW BUSINESS (owner 2026-10-28, migration 335). Every key is optional:
   * `undefined` is a server or a statement predating it, and a rate of zero is
   * a scheme that never paid it. Either way the panel renders exactly as it
   * did before — nothing below appears.
   */
  const newBusinessRate = statement.newBusinessRatePercent;
  const showNewBusinessRate = newBusinessRate !== undefined && isPositiveAmount(newBusinessRate);
  const newBusinessAccounts = statement.newBusinessAccounts ?? [];
  const showNewBusiness =
    showNewBusinessRate ||
    (statement.newBusinessCommission !== undefined &&
      isPositiveAmount(statement.newBusinessCommission));
  const previousWindow =
    statement.newBusinessPreviousStartsOn && statement.newBusinessPreviousEndsOn
      ? `${formatDateOnly(statement.newBusinessPreviousStartsOn)} to ${formatDateOnly(statement.newBusinessPreviousEndsOn)}`
      : null;
  const newBusinessExcluded = EXCLUDED_DOCUMENTS_NOTE(statement.newBusinessExcludedDocuments ?? 0);
  const newBusinessGateBasis = statement.newBusinessGateBasis ?? null;
  const showNewBusinessRecovered = statement.clawbacks.some(
    (clawback) =>
      clawback.newBusinessRecovered !== undefined && isPositiveAmount(clawback.newBusinessRecovered),
  );

  const personalTone = attainmentTone(
    statement.personalAttainmentPercent,
    statement.personalGatePercent,
  );
  const regionalTone = attainmentTone(
    statement.regionalAttainmentPercent,
    statement.regionalGatePercent,
  );

  /*
   * ⛔ ONE OF THE PAIR, OR NEITHER. See `runApproved`.
   */
  const standingNote =
    runApproved === true
      ? FROZEN_STATEMENT_NOTE
      : runApproved === false
        ? LIVE_ATTRIBUTION_NOTE
        : null;

  return (
    <Panel className={className}>
      <PanelHeader
        title={`${leadName} — ${statement.period.name}`}
        description={`Paid in ${statement.currency}`}
      />

      <PanelBody className="grid min-w-0 gap-4">
        {/*
          ⛔ NOT HELPER TEXT — the documented fourth kind: A FACT ABOUT THE DATA
          THE READER CANNOT OTHERWISE SEE, and a helper-text sweep must not delete
          it. Whether these figures can still move is the first thing somebody
          needs to know about them, and nothing else on screen says it.
        */}
        {standingNote && (
          <p className="text-xs text-fg-muted" title={standingNote.detail}>
            {standingNote.short}
          </p>
        )}

        <Block title="Terms, as they stood when this ran">
          <Facts>
            <Fact label="Model" value={statement.modelSlug} />
            <Fact
              label="Personal commission"
              value={
                statement.netsPersonalFromPool
                  ? 'Netted from the regional pool'
                  : 'Paid alongside the regional pool'
              }
            />
            <Fact label="Gate shape" value={statement.gateShapeSlug} />
            <Fact label="Gate" value={statement.isSlidingGate ? 'Sliding' : 'Cliff'} />
            <Fact label="Personal rate" value={formatPercent(statement.personalRatePercent)} />
            <Fact label="Regional rate" value={formatPercent(statement.regionalRatePercent)} />
            <Fact label="Personal gate" value={formatPercent(statement.personalGatePercent)} />
            <Fact label="Regional gate" value={formatPercent(statement.regionalGatePercent)} />
            {showNewBusinessRate && (
              <Fact label={NEW_BUSINESS_HEADINGS.rate} value={formatPercent(newBusinessRate)} />
            )}
            <Fact
              label="Clawback window"
              value={
                statement.clawbackWindowMonths === 0
                  ? 'No clawback'
                  : pluralise(statement.clawbackWindowMonths, 'month')
              }
            />
            <Fact
              label="Draft invoices"
              value={statement.includedDraftInvoices ? 'Counted' : 'Not counted'}
            />
            {/*
              ⚠️ AN AUDIT COPY, RENDERED AS TEXT AND NEVER PARSED. The bands this
              scheme measured, comma-joined at compute so a statement read years
              later says what it counted without resolving a set that may since
              have changed. Anything reading this string as data is matching on
              free text by another name — a query about bands reads
              `f_commission_scheme_regions`.
            */}
            <Fact label="Bands measured" value={orDash(statement.billingRegionsSnapshot)} span />
          </Facts>
        </Block>

        <Block title="The window this covered">
          <Facts>
            <Fact
              label="Financial year"
              value={`${formatDateOnly(statement.periodStartsOn)} to ${formatDateOnly(statement.periodEndsOn)}`}
              span
            />
            {/*
              ⛔ THE WINDOW REVENUE WAS COUNTED OVER — the period intersected with
              the assignment's own dates, and NOT the period. A part-year joiner
              earns against the half they served; counting a full year against a
              halved target would put them over the gate on work done before they
              arrived.
            */}
            <Fact
              label="Counted over"
              value={`${formatDateOnly(statement.windowStartsOn)} to ${formatDateOnly(statement.windowEndsOn)}`}
              span
            />
            <Fact
              label="Months served"
              value={`${statement.monthsServed} of ${statement.monthsInPeriod}`}
            />
            <Fact label="Pro-rating factor" value={statement.prorationFactor} />
            {/*
              The window new business compared against: each bound of "Counted
              over" one calendar year back, frozen at compute.
            */}
            {showNewBusiness && previousWindow && (
              <Fact label={NEW_BUSINESS_HEADINGS.previousWindow} value={previousWindow} span />
            )}
          </Facts>
        </Block>

        <Block title="Attainment">
          <Facts>
            <Fact
              label="Personal revenue"
              value={money(statement.personalRevenue)}
              note={personalExcluded}
            />
            <Fact
              label="Personal target"
              value={money(statement.effectivePersonalTarget)}
              hint={
                statement.effectivePersonalTarget === statement.personalTarget
                  ? undefined
                  : `Full year ${money(statement.personalTarget)}`
              }
            />
            <Fact
              label="Personal attainment"
              value={
                <Badge tone={TONE_BADGE[personalTone]}>
                  {toneLabel(formatPercent(statement.personalAttainmentPercent), personalTone)}
                </Badge>
              }
              span
            />

            <Fact
              label="Regional revenue"
              value={money(statement.regionalRevenue)}
              note={regionalExcluded}
            />
            {/*
              ⛔ NO REGIONAL COMMITMENT IS NOT A TARGET OF ZERO, and the two must
              never render alike. `null` says the question was not asked, the pool
              was not computed, and the gate was not tested against one. A `0`
              here would read as total failure against a commitment nobody made.
            */}
            <Fact
              label="Regional target"
              value={
                statement.effectiveRegionalTarget === null
                  ? 'No regional commitment'
                  : money(statement.effectiveRegionalTarget)
              }
              hint={
                statement.regionalTarget === null ||
                statement.regionalTarget === statement.effectiveRegionalTarget
                  ? undefined
                  : `Full year ${money(statement.regionalTarget)}`
              }
            />
            <Fact
              label="Regional attainment"
              value={
                statement.regionalAttainmentPercent === null ? (
                  'Not measured'
                ) : (
                  <Badge tone={TONE_BADGE[regionalTone]}>
                    {toneLabel(formatPercent(statement.regionalAttainmentPercent), regionalTone)}
                  </Badge>
                )
              }
              span
            />
          </Facts>

          {/*
            The gate in one sentence, worded from the FACTOR. `gateMet` answers
            *did the pool open fully*, so on a sliding scheme it is false for every
            gate between shut and fully open — wording from it would print "gate
            not met" over a pool that really did pay.
          */}
          <p className="mt-3 text-xs text-fg-secondary">{gateSummary(statement)}</p>
        </Block>

        {/*
          ⛔ FROZEN PER-ACCOUNT ROWS, copied at compute (migration 336). Never
          re-fetched from the live breakdown endpoint: that one measures today,
          and this is what was paid.
        */}
        {showNewBusiness && (
          <Block title={NEW_BUSINESS_HEADINGS.newBusiness}>
            <Facts>
              <Fact
                label={NEW_BUSINESS_HEADINGS.accountsThisPeriod}
                value={money(statement.newBusinessAccountsThisPeriod ?? null)}
                note={newBusinessExcluded}
              />
              <Fact
                label={NEW_BUSINESS_HEADINGS.accountsLastPeriod}
                value={money(statement.newBusinessAccountsLastPeriod ?? null)}
              />
              <Fact
                label={NEW_BUSINESS_HEADINGS.newBusiness}
                value={money(statement.newBusiness ?? null)}
              />
              <Fact
                label={NEW_BUSINESS_HEADINGS.newBusinessCommission}
                value={money(statement.newBusinessCommission ?? null)}
              />
              {/*
                Frozen new business gate terms (owner 2026-10-28, migration 339):
                the EFFECTIVE values the run used. `null` is a statement computed
                before the gate was configurable and `undefined` an older API;
                neither renders these facts.
              */}
              {newBusinessGateBasis !== null && (
                <>
                  <Fact
                    label={NEW_BUSINESS_GATE_HEADINGS.basis}
                    value={newBusinessGateBasisLabel(newBusinessGateBasis)}
                  />
                  <Fact
                    label={NEW_BUSINESS_GATE_HEADINGS.personalGate}
                    value={formatPercent(statement.newBusinessPersonalGatePercent ?? null)}
                  />
                  <Fact
                    label={NEW_BUSINESS_GATE_HEADINGS.regionalGate}
                    value={formatPercent(statement.newBusinessRegionalGatePercent ?? null)}
                  />
                  <Fact
                    label={NEW_BUSINESS_GATE_HEADINGS.shape}
                    value={orDash(statement.newBusinessGateShape?.name ?? null)}
                  />
                  <Fact
                    label={NEW_BUSINESS_GATE_HEADINGS.factor}
                    value={formatPercent(factorAsPercent(statement.newBusinessGateFactor ?? null))}
                  />
                </>
              )}
            </Facts>
            {statement.newBusinessAccounts !== undefined && (
              <div className="mt-3 min-w-0">
                <h4 className="mb-2 text-2xs font-semibold text-fg-muted">
                  {NEW_BUSINESS_HEADINGS.accounts}
                </h4>
                <NewBusinessAccountsTable
                  rows={newBusinessAccounts}
                  currency={statement.currency}
                  showCommission
                />
              </div>
            )}
          </Block>
        )}

        <Block title="What this pays">
          <dl className="grid min-w-0 gap-1.5">
            {breakdown.map((row) => (
              <div
                key={row.label}
                className={rowClass(row.kind)}
              >
                <dt className="min-w-0 text-xs text-fg-secondary">{row.label}</dt>
                <dd
                  className={
                    row.kind === 'net'
                      ? 'text-sm font-semibold tabular-nums text-fg'
                      : 'text-xs tabular-nums text-fg'
                  }
                >
                  {money(row.value)}
                </dd>
              </div>
            ))}
          </dl>
        </Block>

        {statement.clawbacks.length > 0 && (
          <Block title="Recovered">
            {/*
              ⛔ NOT HELPER TEXT — the fourth kind, and its own doc names this exact
              placement. Crediting an invoice does NOT reduce the revenue above: a
              credit note is a separate document and never touches the invoice it
              credits, so a fully credited invoice still counts at 100%. The
              natural assumption is the opposite, and a reader who holds it reads
              the rows below as a second deduction on money already removed.
            */}
            <p
              className="mb-2 text-xs text-fg-muted"
              title={CREDIT_DOES_NOT_REDUCE_REVENUE_NOTE.detail}
            >
              {CREDIT_DOES_NOT_REDUCE_REVENUE_NOTE.short}
            </p>

            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              <Table className="min-w-[680px]">
                <THead>
                  <TR>
                    <TH>Kind</TH>
                    <TH>What</TH>
                    <TH>Original</TH>
                    <TH>Restated</TH>
                    <TH>Commission recovered</TH>
                    {showNewBusinessRecovered && (
                      <TH>{NEW_BUSINESS_HEADINGS.newBusinessRecovered}</TH>
                    )}
                  </TR>
                </THead>
                <TBody>
                  {statement.clawbacks.map((clawback) => {
                    /*
                     * ⚠️ A CLAWBACK CAN LAND ON SOMEBODY OTHER THAN THE PERSON WHO
                     * WAS PAID FOR THE ORIGINAL REVENUE, and this cell is the ONLY
                     * place that is visible. An invoice may name its own account
                     * lead; a credit note carries a company and no lead of its own,
                     * so it takes whoever leads that company TODAY. An invoice one
                     * person was credited for, on an account since moved, reduces
                     * somebody else — both numbers internally consistent, neither
                     * screen looking wrong.
                     *
                     * Shown only when it DIFFERS, so the row says something when it
                     * says anything at all.
                     */
                    /*
                     * 🔴 SINCE 2026-09-24 A CLAWBACK IS CHARGED TO WHOEVER WAS
                     * PAID on the revenue, so a set `sourceUserId` normally IS
                     * this statement's lead and says nothing. The branch below
                     * survives for a statement computed before that rule; the
                     * case worth a line now is the FALLBACK — `null`, where no
                     * approved statement counted the document and the server
                     * charged the current account lead instead.
                     */
                    const source = clawback.sourceUserId;
                    const attributedElsewhere = source !== null && source !== statement.user.id;
                    const sourceLead = attributedElsewhere
                      ? (resolveLeadName?.(source) ?? `Lead #${source}`)
                      : null;
                    const fallback = clawbackAttributionNote(source);

                    return (
                      <TR key={clawback.id}>
                        <TD>{CLAWBACK_KIND_LABELS[clawback.kind] ?? clawback.kind}</TD>
                        <TD>
                          <span className="block">{orDash(clawback.description)}</span>
                          {sourceLead && (
                            <span className="mt-0.5 block text-2xs text-warning">
                              Earned against {sourceLead}
                            </span>
                          )}
                          {fallback && (
                            <span className="mt-0.5 block text-2xs text-fg-muted">{fallback}</span>
                          )}
                          {clawback.rateDate && (
                            <span className="mt-0.5 block text-2xs text-fg-muted">
                              Restated at {formatDateOnly(clawback.rateDate)}
                            </span>
                          )}
                        </TD>
                        <TD className="tabular-nums">
                          {formatDecimalString(clawback.originalAmount)}{' '}
                          {clawback.originalCurrency}
                        </TD>
                        <TD className="tabular-nums">{money(clawback.amount)}</TD>
                        <TD className="tabular-nums">{money(clawback.commissionRecovered)}</TD>
                        {/* A component of Commission recovered, never added to it. */}
                        {showNewBusinessRecovered && (
                          <TD className="tabular-nums">
                            {money(clawback.newBusinessRecovered ?? null)}
                          </TD>
                        )}
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>
          </Block>
        )}

        {/*
          The refunds behind "Clawback refunded" — migration 235. A separate
          block and never rows inside "Recovered": the two lists point in
          opposite directions, and one table holding both would invite a reader
          to add a column that mixes money taken with money given back.
          `?? []` is the deploy-order fold for a server predating 235, which
          refunds nothing — an absent list and an empty one are the same fact.
        */}
        {(statement.refunds ?? []).length > 0 && (
          <Block title="Handed back">
            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              <Table className="min-w-[560px]">
                <THead>
                  <TR>
                    <TH>What</TH>
                    <TH>Original</TH>
                    <TH>Recovered on</TH>
                    <TH>Commission refunded</TH>
                  </TR>
                </THead>
                <TBody>
                  {(statement.refunds ?? []).map((refund) => (
                    <TR key={refund.id}>
                      <TD>
                        <span className="block">{orDash(refund.description)}</span>
                        <span className="mt-0.5 block text-2xs text-fg-muted">
                          Invoice since paid
                        </span>
                      </TD>
                      <TD className="tabular-nums">
                        {formatDecimalString(refund.originalAmount)} {refund.originalCurrency}
                      </TD>
                      <TD>{refund.sourceRunId === null ? '—' : `Run ${refund.sourceRunId}`}</TD>
                      <TD className="tabular-nums">{money(refund.commissionRefunded)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          </Block>
        )}

        {statement.notes && (
          <Block title="Notes">
            <p className="text-xs whitespace-pre-line text-fg-secondary">{statement.notes}</p>
          </Block>
        )}
      </PanelBody>
    </Panel>
  );
}

/**
 * The percentage, and the word that keeps the colour from being the only signal.
 * `'unknown'` adds nothing — there is no judgement to report.
 */
/**
 * A presence test on an exact decimal string — text, never `Number()`. `'0.00'`
 * and a negative are false.
 */
function isPositiveAmount(value: string): boolean {
  const text = value.trim();
  if (text === '' || text.startsWith('-')) return false;
  return /[1-9]/.test(text);
}

function toneLabel(percent: string, tone: AttainmentTone): string {
  const word = TONE_WORD[tone];
  return word === null ? percent : `${percent} · ${word}`;
}

/**
 * ⛔ `'note'` IS THE ONE KIND THAT IS NOT PART OF THE SUM, and it must not be
 * styled like a `'deduction'`. It is what the floor at zero left behind — the
 * part of the clawback this year could not absorb — so a reader invited to
 * subtract it again would take it off a net that was already reduced by it. A
 * rule above it and a muted foreground are what separate it from the column.
 *
 * ⛔ And it is never worded as "carried forward" or "owed": whether it carries
 * into the next year is a decision nobody has taken, and "owed" would read as a
 * debt a colleague has to the company, which the payout floor exists to make
 * inexpressible.
 */
function rowClass(kind: PayoutRowKind): string {
  const base = 'flex min-w-0 flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5';
  if (kind === 'net') return `${base} border-t border-hairline-strong pt-1.5`;
  if (kind === 'note') return `${base} mt-1 border-t border-border pt-1.5 text-fg-muted`;
  return base;
}

/* -------------------------------------------------------------------------- */
/* Small layout pieces — one column at 320, two from `sm`                     */
/* -------------------------------------------------------------------------- */

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="min-w-0">
      <h3 className="mb-2 text-xs font-semibold text-fg">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Facts({ children }: { children: React.ReactNode }) {
  return <dl className="grid min-w-0 gap-x-4 gap-y-2 sm:grid-cols-2">{children}</dl>;
}

function Fact({
  label,
  value,
  hint,
  note,
  span,
}: {
  label: string;
  value: React.ReactNode;
  /**
   * A second FIGURE, never advice — the full-year target behind a pro-rated one.
   * A reader cannot otherwise tell a halved target from a small one.
   */
  hint?: string;
  /**
   * A fact that qualifies this figure — today, only the excluded-documents
   * sentence, which says the figure is SHORT and why. Toned as a warning because
   * it reports a gap in the data rather than a property of the business.
   */
  note?: string | null;
  span?: boolean;
}) {
  return (
    <div className={span ? 'min-w-0 sm:col-span-2' : 'min-w-0'}>
      <dt className="text-2xs text-fg-muted">{label}</dt>
      <dd className="text-xs break-words text-fg">{value}</dd>
      {hint && <dd className="text-2xs text-fg-subtle">{hint}</dd>}
      {note && (
        <dd className="mt-1 rounded-sm border-l-2 border-l-warning bg-surface-sunken px-2 py-1 text-2xs text-fg-secondary">
          {note}
        </dd>
      )}
    </div>
  );
}
